import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from './Tooltip';
import { collection, query, orderBy, limit, onSnapshot, updateDoc, doc, arrayUnion, arrayRemove, writeBatch, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { useChatbot } from '../chatbot/ChatbotContext';
import { GamificationWidget } from '../gamification/GamificationWidget';

const Header = ({ user, onLogout, title, mobileMenuOpen, setMobileMenuOpen }) => {
    const navigate = useNavigate();
    const { openChatbot } = useChatbot();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('recent');
    const dropdownRef = useRef();

    useEffect(() => {
        if (!user) return;
        // subscribe to recent notifications (client-side filter)
        const q = query(collection(db, collectionPath('notifications')),
            orderBy('createdAt', 'desc'),
            limit(50)
        );
        const unsub = onSnapshot(q, (snap) => {
            const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Filter for recipients: empty (broadcast) or includes current user id
            const visible = docs.filter(n => !Array.isArray(n.recipients) || n.recipients.length === 0 || n.recipients.includes(user.uid) || n.recipients.includes(user.id) );
            setNotifications(visible);
            // Count unread notifications that have NOT been dismissed
            const unread = visible.filter(n => 
                (!Array.isArray(n.readBy) || !n.readBy.includes(user.uid)) &&
                (!Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(user.uid))
            ).length;
            setUnreadCount(unread);
        });

        return () => unsub();
    }, [user]);

    useEffect(() => {
        const onClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, []);

    const markAsRead = async (notification) => {
        try {
            await updateDoc(doc(db, collectionPath('notifications'), notification.id), { readBy: arrayUnion(user.uid) });
        } catch (e) {
            console.warn('markAsRead failed', e);
        }
    };

    const markAsUnread = async (notification) => {
        try {
            await updateDoc(doc(db, collectionPath('notifications'), notification.id), { readBy: arrayRemove(user.uid) });
        } catch (e) {
            console.warn('markAsUnread failed', e);
        }
    };

    const dismissNotification = async (notification) => {
        try {
            await updateDoc(doc(db, collectionPath('notifications'), notification.id), { dismissedBy: arrayUnion(user.uid) });
        } catch (e) {
            console.warn('dismissNotification failed', e);
        }
    };

    const bulkMarkRead = async () => {
        const batch = writeBatch(db);
        filteredNotifications.forEach(n => {
            const isUnread = !Array.isArray(n.readBy) || !n.readBy.includes(user.uid);
            if (isUnread) {
                batch.update(doc(db, collectionPath('notifications'), n.id), { readBy: arrayUnion(user.uid) });
            }
        });
        try {
            await batch.commit();
        } catch (e) {
            console.warn('bulkMarkRead failed', e);
        }
    };

    const bulkMarkUnread = async () => {
        const batch = writeBatch(db);
        filteredNotifications.forEach(n => {
            const isRead = Array.isArray(n.readBy) && n.readBy.includes(user.uid);
            if (isRead) {
                batch.update(doc(db, collectionPath('notifications'), n.id), { readBy: arrayRemove(user.uid) });
            }
        });
        try {
            await batch.commit();
        } catch (e) {
            console.warn('bulkMarkUnread failed', e);
        }
    };

    const bulkDismiss = async () => {
        const batch = writeBatch(db);
        filteredNotifications.forEach(n => {
            batch.update(doc(db, collectionPath('notifications'), n.id), { dismissedBy: arrayUnion(user.uid) });
        });
        try {
            await batch.commit();
        } catch (e) {
            console.warn('bulkDismiss failed', e);
        }
    };

    // Filter notifications based on active tab and exclude dismissed
    const filteredNotifications = notifications.filter(n => {
        const isDismissed = Array.isArray(n.dismissedBy) && n.dismissedBy.includes(user.uid);
        if (isDismissed) return false;
        
        if (activeTab === 'recent') return true;
        return n.level === 'warning' || n.level === 'urgent' || n.level === 'important';
    });

    return (
        <header className="flex items-center justify-between h-16 px-6 bg-white border-b">
            <div className="flex items-center space-x-4">
                {/* Hamburger Menu Button - Mobile Only */}
                <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Toggle menu"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        {mobileMenuOpen ? (
                            // X icon when menu is open
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            // Hamburger icon when menu is closed
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </button>
                
                <h1 className="text-xl font-semibold">{title}</h1>
            </div>
            <div className="flex items-center space-x-4">
                {/* Gamification Widget */}
                <GamificationWidget />
                
                {/* Gamification/Leaderboard Icon */}
                <Tooltip text="Leaderboard & Achievements" position="bottom">
                    <button
                        onClick={() => navigate('/leaderboard')}
                        className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 p-0"
                        aria-label="Leaderboard & Achievements"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                            {/* Trophy icon */}
                            <circle cx="12" cy="12" r="9" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5"/>
                            {/* Trophy cup */}
                            <path d="M8 7h8v4c0 2.21-1.79 4-4 4s-4-1.79-4-4V7z" fill="#F59E0B" stroke="#D97706" strokeWidth="1"/>
                            {/* Trophy handles */}
                            <path d="M8 8H6.5a1.5 1.5 0 000 3H8" stroke="#D97706" strokeWidth="1" fill="none"/>
                            <path d="M16 8h1.5a1.5 1.5 0 010 3H16" stroke="#D97706" strokeWidth="1" fill="none"/>
                            {/* Trophy base */}
                            <path d="M10 15v1h4v-1" stroke="#D97706" strokeWidth="1" fill="none"/>
                            <path d="M9 16h6v1H9z" fill="#D97706"/>
                        </svg>
                    </button>
                </Tooltip>
                
                {/* AI Chatbot Icon - Always visible */}
                <Tooltip text="Open AI Assistant" position="bottom">
                    <button
                        onClick={openChatbot}
                        className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 p-0 relative"
                        aria-label="Open AI Assistant"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" fill="#8B5CF6" stroke="#7C3AED" strokeWidth="1.5"/>
                            <path d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16.5 10.5c0-2.21-2.015-4-4.5-4s-4.5 1.79-4.5 4c0 1.657 1.168 3.068 2.813 3.654l-.063.846a.75.75 0 001.25.6l1.5-1.25c.167-.14.25-.35.25-.568C14.5 13.45 16.5 12.108 16.5 10.5z" stroke="white" strokeWidth="1.2" fill="none"/>
                        </svg>
                    </button>
                </Tooltip>

                {/* Support Icon */}
                <Tooltip text="Help & Support" position="bottom">
                    <button
                        onClick={() => navigate('/support')}
                        className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 p-0"
                        aria-label="Help & Support"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                            {/* Question mark circle - matching sidebar style */}
                            <circle cx="12" cy="12" r="9" fill="#10B981" stroke="#059669" strokeWidth="1.5"/>
                            <path d="M12 17h.01" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                            <path d="M12 13.5v-.5a2 2 0 10-2-2" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </Tooltip>

                {/* Notifications */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 p-0 relative"
                        aria-label="Notifications"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                            {/* Bell icon - matching sidebar style */}
                            {/* Bell body */}
                            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            {/* Bell bottom */}
                            <path d="M9 17v1a3 3 0 106 0v-1" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">{unreadCount}</span>
                        )}
                    </button>

                    {dropdownOpen && (
                        <div className="absolute right-0 mt-2 w-96 bg-white border rounded shadow-lg z-50">
                            <div className="flex border-b">
                                <button className={`flex-1 px-3 py-2 text-sm ${activeTab==='recent' ? 'font-semibold' : 'text-gray-600'}`} onClick={() => setActiveTab('recent')}>Recent</button>
                                <button className={`flex-1 px-3 py-2 text-sm ${activeTab==='important' ? 'font-semibold' : 'text-gray-600'}`} onClick={() => setActiveTab('important')}>Important</button>
                            </div>
                            <div className="max-h-[32rem] overflow-auto">
                                {filteredNotifications.length === 0 && (
                                    <div className="p-3 text-sm text-gray-500">No notifications</div>
                                )}
                                {filteredNotifications.slice(0,20).map(n => {
                                    const createdAt = n.createdAt && n.createdAt.seconds ? new Date(n.createdAt.seconds * 1000) : (n.createdAt ? new Date(n.createdAt) : null);
                                    const isUnread = !Array.isArray(n.readBy) || !n.readBy.includes(user.uid);
                                    const isDismissed = Array.isArray(n.dismissedBy) && n.dismissedBy.includes(user.uid);
                                    
                                    if (isDismissed) return null; // Don't show dismissed notifications
                                    
                                    return (
                                        <div key={n.id} className={`p-3 border-b hover:bg-gray-50 ${isUnread ? 'bg-yellow-50' : ''}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="text-sm font-medium">{n.title}</div>
                                                <div className="text-xs text-gray-400">{createdAt ? createdAt.toLocaleString() : ''}</div>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1">{(n.body || '').slice(0,140)}</div>
                                            <div className="mt-2 flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    {isUnread ? (
                                                        <button className="text-xs text-indigo-600 hover:underline" onClick={async () => { await markAsRead(n); }}>Mark Read</button>
                                                    ) : (
                                                        <button className="text-xs text-gray-600 hover:underline" onClick={async () => { await markAsUnread(n); }}>Mark Unread</button>
                                                    )}
                                                    <button className="text-xs text-red-600 hover:underline" onClick={async () => { await dismissNotification(n); }}>Dismiss</button>
                                                    {n.related && n.related.type && (
                                                        <button 
                                                            className="text-xs text-indigo-600 hover:underline font-medium" 
                                                            onClick={() => { 
                                                                markAsRead(n); 
                                                                setDropdownOpen(false); 
                                                                const entityId = n.related.id;
                                                                const route = n.related.type === 'Opportunity' 
                                                                    ? (entityId ? `/opportunities/${entityId}` : '/opportunities')
                                                                    : n.related.type === 'Task' 
                                                                        ? (entityId ? `/tasks/${entityId}` : '/tasks')
                                                                    : n.related.type === 'customer' 
                                                                        ? (entityId ? `/customer/${entityId}` : '/customers')
                                                                    : n.related.type === 'Order' 
                                                                        ? (entityId ? `/orders/${entityId}` : '/orders')
                                                                    : '/dashboard';
                                                                navigate(route);
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Bulk Actions */}
                            {filteredNotifications.length > 0 && (() => {
                                const hasUnread = filteredNotifications.some(n => 
                                    !Array.isArray(n.readBy) || !n.readBy.includes(user.uid)
                                );
                                return (
                                    <div className="p-2 border-t bg-gray-50 flex items-center justify-between">
                                        <button 
                                            className={`text-xs ${hasUnread ? 'text-indigo-600' : 'text-gray-600'} hover:underline`}
                                            onClick={hasUnread ? bulkMarkRead : bulkMarkUnread}
                                        >
                                            {hasUnread ? 'Mark All Read' : 'Mark All Unread'}
                                        </button>
                                        <button 
                                            className="text-xs text-red-600 hover:underline"
                                            onClick={bulkDismiss}
                                        >
                                            Dismiss All
                                        </button>
                                    </div>
                                );
                            })()}
                            
                            {/* View All Link */}
                            <div className="p-2 border-t bg-gray-100">
                                <button 
                                    className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-semibold"
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        navigate('/notifications');
                                    }}
                                >
                                    View All Notifications →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* View Profile Section */}
                <div
                    className="flex items-center space-x-2 cursor-pointer"
                    onClick={() => navigate('/profile')}
                    title="View Profile"
                >
                    <div className="h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold">
                        {(user?.firstName || user?.lastName)
                            ? `${(user.firstName || '').slice(0,1).toUpperCase()}${(user.lastName || '').slice(0,1).toUpperCase()}`
                            : (user?.displayName || user?.email || '?').split(/\s+/).slice(0,2).map(n => n[0]?.toUpperCase()).join('')}
                    </div>
                    <div className="hidden md:block">
                        <div className="text-sm font-medium">{user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email}</div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                </div>
                <button
                    onClick={onLogout}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-500"
                >
                    Logout
                </button>
            </div>
        </header>
    );
};

export default Header;
